<script lang="ts" setup>
import Top from '~/components/Top/Top.vue'
import UiList from '@/components/UiList/UiList.vue'
import { useOurApproach } from '@/domains/esg/composables/content/useOurApproach'
import { EUI_WITH_ICONS_LAYOUTS } from '@/components/UiListWithIcons/UiListWithIcons.types'

import UiDrawer from '@/components/UiDrawer/UiDrawer.vue'

const { t, locale } = useI18n()
const { certificationList, ethicsList, companyValuesDrawer, irregularitiesDrawer1, irregularitiesDrawer2 } = useOurApproach()

const activeDrawersCompanyValues = ref([0])
const activeDrawersIrregularities1 = ref([0])
const activeDrawersIrregularities2 = ref([0])

const toggleCompanyValuesDrawer = (index: number) => {
  if (activeDrawersCompanyValues.value.includes(index)) {
    activeDrawersCompanyValues.value = activeDrawersCompanyValues.value.filter(el => el !== index)
  } else {
    activeDrawersCompanyValues.value.push(index)
  }
}

const toggleIrregularities1Drawer = (index: number) => {
  if (activeDrawersIrregularities1.value.includes(index)) {
    activeDrawersIrregularities1.value = activeDrawersIrregularities1.value.filter(el => el !== index)
  } else {
    activeDrawersIrregularities1.value.push(index)
  }
}

const toggleIrregularities2Drawer = (index: number) => {
  if (activeDrawersIrregularities2.value.includes(index)) {
    activeDrawersIrregularities2.value = activeDrawersIrregularities2.value.filter(el => el !== index)
  } else {
    activeDrawersIrregularities2.value.push(index)
  }
}

useHead({
  title: t('pageTitles.ourApproach.title'),
  meta: [
    {
      name: 'description',
      content: t('pageTitles.ourApproach.meta'),
    },
  ],
  link: [
    {
      hid: 'favicon',
      rel: 'icon',
      type: 'image/x-icon',
      href: '/favicon.ico',
    },
    {
      rel: 'canonical',
      href: t(`pageTitles.ourApproach.canonical.${locale.value}`),
    },
    {
      rel: 'alternate',
      hreflang: 'pl',
      href: t('pageTitles.ourApproach.canonical.pl'),
    },
    {
      rel: 'alternate',
      hreflang: 'en',
      href: t('pageTitles.ourApproach.canonical.en'),
    },
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: t('pageTitles.ourApproach.canonical.pl'),
    },
  ],
})

</script>

<template>
  <div>
    <Top
      :header="$t('ourApproach.top.header')"
      :subheader="$t('ourApproach.top.subheader')"
      :text="$t('ourApproach.top.text')"
    />

    <TextWithImage
      id="companys-articles-of-association"
      class="section1"
      :header="$t('ourApproach.section1.header')"
      :text="$t('ourApproach.section1.text')"
      :text-second="$t('ourApproach.section1.textSecond')"
      image="/images/responsible-business/benefitsystems-responsible-business-our-approach_d.webp"
      :button="{
        text: $t('ourApproach.section1.button.text'),
        to: $t('ourApproach.section1.button.link'),
        icon: '/icons/icon-download.svg',
        variant: 'primary',
        target: '_blank'
      }"
    />

    <UiSection
      id="company-values"
      color="psychology-100"
      class="section-values"
    >
      <UiHeader
        :header="$t('ourApproach.sectionValues.header')"
        :subheader="$t('ourApproach.sectionValues.text')"
        position="center"
        header-size="body-1"
      />

      <div class="section-container">
        <div class="section-row">
          <div class="section-col __img">
            <img
              :alt="$t('ourApproach.sectionValues.header')"
              src="/images/our-approach/benefitsystems-responsible-business-our-approach-company-values.webp"
            >
          </div>

          <div class="section-col">
            <UiDrawer
              v-for="(drawer, index) in companyValuesDrawer"
              :key="drawer.header"
              :header="drawer.header"
              :description="drawer.description"
              :variant="'tertiary'"
              :icon="drawer.icon"
              :is-open="activeDrawersCompanyValues.includes(index)"
              @clicked="() => { toggleCompanyValuesDrawer(index) }"
            />
          </div>
        </div>
      </div>
    </UiSection>

    <UiSection
      id="b-corp"
      color="white"
      class="section-bcorp"
    >
      <UiHeader
        :header="$t('ourApproach.sectionBcorp.header')"
        position="center"
        header-size="body-1"
      />
      <div class="section-container">
        <div class="section-row">
          <a
            :href="$t('ourApproach.sectionBcorp.button.link')"
            target="_blank"
            rel="noopener noreferrer"
          >
            <picture>
              <source
                media="(width < 1200px)"
                srcset="/images/our-approach/benefitsystems-responsible-business-our-approach-bcorp_m.webp"
              >
              <source
                media="(width >= 1200px)"
                srcset="/images/our-approach/benefitsystems-responsible-business-our-approach-bcorp.webp"
              >
              <img
                src="/images/our-approach/benefitsystems-responsible-business-our-approach-bcorp.webp"
                :alt="$t('ourApproach.sectionBcorp.header')"
              >
            </picture>
          </a>
        </div>

        <div class="section-row __b-corp">
          <div class="section-col">
            <h3 class="section-heading__h3">
              {{ $t('ourApproach.sectionBcorp.text') }}
            </h3>
          </div>

          <div class="section-col">
            <h4 class="section-heading__h4">
              {{ $t('ourApproach.sectionBcorp.certification.header') }}
            </h4>

            <UiList
              class="__certification"
              :list="certificationList"
            />

            <UiButton
              class="section-button"
              :target="'_blank'"
              :to="$t('ourApproach.sectionBcorp.button.link')"
            >
              {{ $t('ourApproach.sectionBcorp.button.text') }}
            </UiButton>
          </div>
        </div>
      </div>
    </UiSection>

    <UiSection
      id="code-of-ethics"
      color="psychology-100"
      class="section-ethics"
    >
      <UiHeader
        :header="$t('ourApproach.sectionEthics.header')"
        :subheader="$t('ourApproach.sectionEthics.subheader')"
        position="center"
        header-size="body-1"
      />

      <div class="section-container">
        <div class="section-row">
          <div class="section-col __list-with-icons">
            <UiListWithIcons
              v-if="ethicsList.length"
              :list="ethicsList"
              :layout="EUI_WITH_ICONS_LAYOUTS.COLUMN"
              class="__ethics"
            />

            <UiButton
              class="section-button"
              :target="'_blank'"
              :to="$t('ourApproach.sectionEthics.button.link')"
            >
              {{ $t('ourApproach.sectionEthics.button.text') }}
            </UiButton>
          </div>

          <div class="section-col __image">
            <img
              :alt="$t('ourApproach.sectionEthics.header')"
              src="/images/our-approach/benefitsystems-responsible-business-our-approach-ehics-code.webp"
            >
          </div>
        </div>
      </div>
    </UiSection>
    
    <UiSection
      id="reporting-irregularities"
      color="gray"
      class="section-irregularities"
    >
      <UiHeader
        :header="$t('ourApproach.sectionIrregularities.header')"
        :subheader="$t('ourApproach.sectionIrregularities.subheader1')"
        position="center"
      />

      <div class="section-container">
        <div class="section-row">
          <div class="section-col">
            <UiDrawer
              v-for="(drawer, index) in irregularitiesDrawer1"
              :key="drawer.header"
              class="SingleDrawer__irregularities"
              :header="drawer.header"
              :description="drawer.description"
              :variant="'tertiary'"
              :icon="drawer.icon"
              :is-open="activeDrawersIrregularities1.includes(index)"
              @clicked="() => { toggleIrregularities1Drawer(index) }"
            />
          </div>
        </div>
      </div>

      <UiHeader
        header=""
        :subheader="$t('ourApproach.sectionIrregularities.subheader2')"
        position="center"
      />

      <div class="section-container">
        <div class="section-row">
          <div class="section-col">
            <UiDrawer
              v-for="(drawer, index) in irregularitiesDrawer2"
              :key="drawer.header"
              class="SingleDrawer__irregularities"
              :header="drawer.header"
              :description="drawer.description"
              :variant="'tertiary'"
              :icon="drawer.icon"
              :is-open="activeDrawersIrregularities2.includes(index)"
              @clicked="() => { toggleIrregularities2Drawer(index) }"
            />
          </div>
        </div>
      </div>
    </UiSection>
  </div>
</template>

<style lang="scss" scoped>
:deep(.top) {
  min-height: 55.4rem;
}

:deep(.section) {
  h3 {
    margin-bottom: 3.2rem;
    @include h10;
  }
}

.section-container {
  @include section-centered();
  display: flex;
  flex-direction: column;
}

.section-row {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 3.2rem;
  margin-bottom: 3.2rem;

  &.__b-corp {
    gap: 0;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
}

.section-col {
  flex: 1;

  &.__image {
    order: -1;
    text-align: center;
  }
}

.section-button {
  margin: 0 auto;
}

:deep(.list.__certification) {
  margin-top: 3.2rem;
  margin-bottom: 2.4rem;

  .list__item {
    align-items: flex-start;
    width: auto;
  }

  .list__item_text {
    @include h10;
    color: $text-400;
  }
}

:deep(.icon-list) {
  &.__ethics {
    margin-top: 0;
    
    .icon-list__item_text {
      @include h10;
      color: $text-400;
    }

    .icon-list__item {
      align-items: flex-start;
    }
  }
}

.section1 {
  margin-bottom: 0;

  :deep(.text-with-image__actions .button) {
    align-self: flex-start;
  }
}

.section-values {
  margin-bottom: 0;
  border-radius: 0;

  &.section--psychology-100 {
    background-color: $psychology-100;
  }

  .section-col {
    &.__img {
      text-align: center;
    }
  }

  :deep(.ui-header) {
    @include h8;
    margin-bottom: 2.4rem;
  }

  :deep(.ui-header__subheader) {
    @include h10;
    margin-bottom: 3.2rem;
  }
}

.section-bcorp {
  margin-bottom: 0;
  border-radius: 0;

  :deep(.ui-header) {
    @include h8;
    margin-bottom: 2.4rem;
  }

  img {
    display: block;
    margin: 0 auto;
  }

  .section-heading__h3 {
    @include h9;
  }

  .section-heading__h4 {
    @include h10;
    color: $text-300;
  }
}

.section-ethics {
  border-radius: 0;

  &.section--psychology-100 {
    background-color: $psychology-100;
  }

  :deep(.ui-header) {
    @include h8;
    margin-bottom: 2.4rem;
  }

  :deep(.ui-header__subheader) {
    @include h10;
    margin-bottom: 3.2rem;
  }
}

@include atM {

  :deep(.top) {
    .top__item {
      max-width: 65%;
    }
  }

  :deep(.section) {
    @include h8;
    margin-bottom: 6.4rem;
  }

  .section-row {
    flex-direction: row;
    gap: 6.4rem;
    margin-bottom: 6.4rem;

    &.__b-corp {
      gap: 8.4rem;
    }
  }

  .section-col {
    &.__image {
      order: inherit;
    }
  }

  .section-button {
    margin: inherit;
  }

  :deep(.list.__certification) {
     margin-bottom: 4.8rem;
    
     .list__item {
      @include h9;
    }

    .list__item_text {
      @include h9;
    }
  }

  :deep(.icon-list) {
    &.__ethics {
      .icon-list__item_text {
        @include h9;
      }
    }
  }

  .section1 {
    margin-bottom: 0;

    :deep(.ui-header) {
      margin-bottom: 5.6rem;
    }
  }

  .section-values {
    margin-bottom: 0;
    border-radius: 1.6rem;

    :deep(.ui-header) {
      @include h4;
      margin-bottom: 4.8rem;
    }

    :deep(.ui-header__subheader) {
      @include h8;
      max-width: 132rem;
      margin: 0 auto 6.4rem;
    }
  }

  .section-bcorp {
    margin-bottom: 0;
    border-radius: 1.6rem;

    :deep(.ui-header) {
      @include h4;
      margin-bottom: 4.8rem;
    }
  }

  .section-ethics {
    margin-bottom: 0;
    border-top-left-radius: 1.6rem;
    border-top-right-radius: 1.6rem;

    :deep(.ui-header) {
      @include h4;
      margin-bottom: 4.8rem;
    }

    :deep(.ui-header__subheader) {
      @include h8;
      max-width: 132rem;
      margin: 0 auto 6.4rem;
    }
  }
}

.section-irregularities {
  border-top-left-radius: 0;
  border-top-right-radius: 0;

  :deep(.ui-header__subheader) {
    margin-bottom: 3.2rem;
  }
}

:deep(.SingleDrawer__irregularities) {
  .single-drawer__description-wrapper {
    ul li {
      display: block;
    }
  }
}
</style>
